import { z } from 'zod'
import type { DraftStatus } from '@/lib/api/types'

// Draft status badge styles -- per UI-SPEC "Draft Status Badge Colors"
export const draftStatusBadgeStyles: Record<DraftStatus, string> = {
  draft: 'bg-muted text-muted-foreground',
  approved: 'bg-primary/10 text-primary',
  rejected: 'bg-destructive/10 text-destructive',
  published: 'bg-emerald-100 text-emerald-700',
  scheduled: 'bg-amber-100 text-amber-700',
  exported: 'bg-emerald-100 text-emerald-700',
}

export const draftStatusLabels: Record<DraftStatus, string> = {
  draft: 'Draft',
  approved: 'Approved',
  rejected: 'Rejected',
  published: 'Published',
  scheduled: 'Scheduled',
  exported: 'Exported',
}

export const draftStatusFilterOptions = [
  { value: '', label: 'All' },
  { value: 'draft', label: 'Draft' },
  { value: 'approved', label: 'Approved' },
  { value: 'rejected', label: 'Rejected' },
  { value: 'published', label: 'Published' },
] as const

// PRDs have their own lifecycle: draft → published (via the Publish button),
// rejected (via Reject), or exported (orthogonal — set on Drive/ClickUp
// export). 'approved' was sunset in API migration 0017 in favour of an
// explicit 'published' state. The Drafts tab swaps in this list when
// templateKey === 'product', and 'published' rows are filtered OUT of the
// drafts list because they live in the new "Published" tab.
export const prdDraftStatusFilterOptions = [
  { value: '', label: 'All' },
  { value: 'draft', label: 'Draft' },
  { value: 'rejected', label: 'Rejected' },
  { value: 'exported', label: 'Exported' },
] as const

// Zod schema for draft editing validation -- per RESEARCH.md
export const draftEditSchema = z.object({
  title: z.string().min(1, 'Title is required').max(200),
  body: z.string().min(1, 'Body is required'),
  heroImageUrl: z.string().nullable().optional(),
  heroImageAlt: z.string(),
  seo: z.object({
    title: z.string().max(70, 'SEO title should be under 70 characters'),
    description: z.string().max(160, 'SEO description should be under 160 characters'),
    keywords: z.array(z.string()).max(10, 'Maximum 10 keywords'),
  }),
})

export type DraftEditFormData = z.infer<typeof draftEditSchema>

// Zod schema for reject reason validation
export const rejectReasonSchema = z.object({
  reason: z.string().min(10, 'Reason must be at least 10 characters'),
})

export type RejectReasonFormData = z.infer<typeof rejectReasonSchema>

// Zod schema for PRD draft editing -- D-02, body-only slice per Phase 27 CONTEXT.
// Deliberately a separate schema (not a subset of draftEditSchema) so the blog
// shape stays intact and the PRD form contract is explicit.
export const prdEditSchema = z.object({
  body: z.string().min(1, 'Body is required'),
})

export type PrdEditFormData = z.infer<typeof prdEditSchema>
